/**
 * PieceRenderer — the leather-piece render cluster extracted from Scene
 * (#856, epic #828 slice Scene-S3). Scene OWNS a PieceRenderer instance and
 * DRIVES it by composition (it is NOT a base class), mirroring the MatRenderer
 * and ConstructionOverlayRenderer precedents: the renderer holds the piece
 * VISUAL BUILDERS (`renderPiece` and its sub-renderers — outline / holes /
 * cutouts, decorations / stamps / fills / offsets, stitch lines, hardware,
 * plus the leather-clip / hardware / stamp / path helpers used only by them),
 * while the `render()` orchestration and the selection / handle-visibility
 * gating that decides WHEN to call `renderPiece` stay on Scene.
 *
 * Everything the builders read from the owning Scene (the paper scope, the
 * live selection, the edit-mode / construction-active / part-layout view
 * state, and the stamp library + symbol table) is injected through the
 * `PieceRenderHost` interface. Behaviour is identical to the in-Scene original
 * — this is a pure move + host rewiring.
 */
import paper from "paper";
import { PaperOffset } from "paperjs-offset";
import {
  isVisible,
  pieceHardware,
  pieceStitchLines,
  type Decoration,
  type Hardware,
  type Piece,
  type Project,
  type StitchLine,
} from "../model/Project";
import { jointMarksByStitch, stitchJoints } from "../model/stitchJoints";
import { HARDWARE_COLORS } from "../hardware/HardwareCatalog";
import { hardwarePathData, shadeHex } from "../hardware/HardwareSymbols";
import {
  buildLeatherRegion,
  placedStitchHoles,
} from "../model/placedStitchHoles";
import {
  directedStitchLine,
  effectiveStitchSpacing,
  hasOffPitchHoles,
  isStitchLoop,
  marksFromPlacements,
  stitchConnectorPath,
  stitchLineLength,
  stitchRendersClosed,
  stitchWorkingWindow,
} from "../model/stitching";
import { seamPathData, seamPathDataLaps } from "../model/seamGeometry";
import { isFeatureEnabled } from "../featureFlags";
import type { StampLibrary } from "../stamps/StampLibrary";
import type { StampSymbols } from "./StampSymbols";
import { placeStampsAlongPath } from "../stamps/lineStamping";
import { tileRegion } from "../stamps/tileFill";
import { cssVar } from "../colors";
import type { SceneSelection } from "./Scene";

// All piece render colors come from CSS custom properties defined in
// `src/styles.css` (per the AGENTS.md "Theming" rule). `cssVar()` reads the
// live computed value, so a future theme override flows through with no
// further changes here. These mirror the single-definition helpers on Scene;
// each renderer keeps its own thin wrappers (the MatRenderer /
// ConstructionOverlayRenderer precedent).
const SHAPE_STROKE = () => cssVar("--leather-edge");
/** Thin brown outline around every finished part so overlapping parts stay
 *  distinguishable (canvas only — the export keeps its own cut-line colour). */
const LEATHER_BORDER = () => cssVar("--leather-border");
/** View-pixel width of that border — thin, but heavier than the old 0.3 hairline
 *  so an overlap edge actually reads. */
const LEATHER_BORDER_WIDTH = 0.7;
const SHAPE_FILL = () => new paper.Color(cssVar("--leather-fill"));
const HOLE_STROKE = () => cssVar("--piece-cut");
const CUTOUT_STROKE = () => cssVar("--piece-cut");
const STITCH_GUIDE_STROKE = () => cssVar("--piece-cut");
const DECOR_STROKE = () => cssVar("--leather-decor");
const SELECTION_STROKE = () => cssVar("--canvas-accent");
/** Selection accent for marks drawn OVER a leather piece — a near-black
 *  charcoal that reads clearly on the tan leather fill / edge (#744). */
const LEATHER_SELECTION_STROKE = () => cssVar("--canvas-accent-leather");
const CONSTRUCTION_STROKE = () => cssVar("--construction-stroke");

/** The shared Scene state + services the piece builders reach back into. The
 *  paper scope + layer stack stay owned by Scene (shared with the mat /
 *  construction renderers); the live selection, the edit-mode /
 *  construction-active / part-layout view state, and the stamp library +
 *  symbol table stay single-definition on Scene because non-piece code reads
 *  them too. Implemented by Scene. */
export interface PieceRenderHost {
  getPaperScope(): paper.PaperScope;
  getSelection(): SceneSelection;
  getEditMode(): "none" | "structural" | "stitch";
  isConstructionActive(): boolean;
  getPartLayoutView(): "spread" | "assembled";
  getLibrary(): StampLibrary;
  getStampSymbols(): StampSymbols;
  /** The live project — shared-hole joints (#693) are a relationship BETWEEN
   *  parts, so they can't be derived from the one being rendered. */
  getProject(): Project | null;
}

export class PieceRenderer {
  constructor(private host: PieceRenderHost) {}

  /** Shared-hole joints for the current project (#693), memoized on the project
   *  reference: every piece rendered in a pass asks for the same answer, and
   *  the commit pipeline hands out a new ref whenever anything changed. */
  private jointCache: {
    project: Project;
    marks: Map<string, Array<{ index: number; label: string }>>;
  } | null = null;

  private jointMarksFor(stitchId: string): Array<{
    index: number;
    label: string;
  }> {
    const project = this.host.getProject();
    if (!project) return [];
    if (this.jointCache?.project !== project) {
      this.jointCache = {
        project,
        marks: jointMarksByStitch(stitchJoints(project, this.scope)),
      };
    }
    return this.jointCache.marks.get(stitchId) ?? [];
  }

  /** The paper scope is owned by Scene and reached through the host; the piece
   *  builders draw onto the same shared scope. */
  private get scope(): paper.PaperScope {
    return this.host.getPaperScope();
  }
  private get selection(): SceneSelection {
    return this.host.getSelection();
  }
  private get editMode(): "none" | "structural" | "stitch" {
    return this.host.getEditMode();
  }
  private get constructionActive(): boolean {
    return this.host.isConstructionActive();
  }
  private get partLayoutView(): "spread" | "assembled" {
    return this.host.getPartLayoutView();
  }
  private get library(): StampLibrary {
    return this.host.getLibrary();
  }
  private get symbols(): StampSymbols {
    return this.host.getStampSymbols();
  }

  renderPiece(piece: Piece): paper.Group {
    const group = new this.scope.Group();
    group.data = { kind: "piece", pieceId: piece.id };
    group.name = piece.name;

    const sel = this.selection;
    const pieceIds = sel.pieceIds ?? [];
    const partInMulti = pieceIds.includes(piece.id);
    // A piece reads as "selected" when it sits in the multi-select set
    // AND no sub-item of the lead piece is the actual focus. The
    // sub-item gate only applies in single-piece mode — a multi-piece
    // selection clears every sub-item id during normalization, so for
    // additional pieces the gate is automatically satisfied.
    const isSelectedPiece =
      partInMulti &&
      !sel.decorationId &&
      !sel.holeId &&
      !sel.cutoutId &&
      !sel.stitchId &&
      !sel.constructionId &&
      !sel.stitchingScope;

    if (this.editMode === "structural") {
      // Hole / Cutout edit mode: full leather + editable hole / cutout
      // markers on top. Stitch mode falls through to the display branch so
      // the leather looks "finished" and the seam reads against it.
      const shape = this.parsePath(piece.shapePathData);
      if (shape) {
        // A finished (filled) part gets a thin BROWN border so overlapping parts
        // stay distinguishable. Selection + the unfilled construction outline
        // (its own scaffold look) keep their own strokes.
        const brownBorder = !isSelectedPiece && !this.constructionActive;
        shape.strokeColor = new this.scope.Color(
          isSelectedPiece
            ? this.leatherSelectionStroke()
            : brownBorder
              ? LEATHER_BORDER()
              : SHAPE_STROKE(),
        );
        shape.strokeScaling = false;
        // In the construction-editing context the leather reads as an
        // unfilled outline, so give it a heavier solid stroke (the faint
        // hairline only reads when it's backed by a fill) — #649 follow-up.
        shape.strokeWidth = isSelectedPiece
          ? 1.5
          : this.constructionActive
            ? 1.2
            : LEATHER_BORDER_WIDTH;
        shape.fillColor = this.constructionActive ? null : SHAPE_FILL();
        shape.data = { kind: "piece", pieceId: piece.id, role: "shape" };
        group.addChild(shape);
      }

      for (const hole of piece.holes) {
        if (!isVisible(hole)) continue;
        const isSelectedHole =
          sel.pieceId === piece.id && sel.holeId === hole.id;
        const c = new this.scope.Path.Circle(
          new this.scope.Point(hole.position.x, hole.position.y),
          hole.radius,
        );
        c.strokeColor = new this.scope.Color(
          isSelectedHole ? this.leatherSelectionStroke() : HOLE_STROKE(),
        );
        c.strokeWidth = isSelectedHole ? 1.5 : 0.5;
        c.fillColor = new this.scope.Color(0, 0, 0, 0.25);
        c.data = { kind: "hole", pieceId: piece.id, holeId: hole.id };
        group.addChild(c);
      }

      for (const cut of piece.cutouts) {
        if (!isVisible(cut)) continue;
        const isSelectedCutout =
          sel.pieceId === piece.id && sel.cutoutId === cut.id;
        const p = this.parsePath(cut.pathData);
        if (!p) continue;
        p.strokeColor = new this.scope.Color(
          isSelectedCutout ? this.leatherSelectionStroke() : CUTOUT_STROKE(),
        );
        p.strokeWidth = isSelectedCutout ? 1.5 : 1;
        p.fillColor = new this.scope.Color(0, 0, 0, 0.25);
        p.dashArray = [3, 3];
        p.data = { kind: "cutout", pieceId: piece.id, cutoutId: cut.id };
        group.addChild(p);
      }
    } else {
      // Display mode: leather minus visible holes/cutouts as the actual
      // rendered shape — looks like the finished piece. No hole / cutout
      // markers are drawn; selecting either still works through the panel.
      const leather = this.buildLeatherClip(piece);
      if (leather) {
        // Thin brown border on the finished (filled) part so overlaps read (see
        // the structural branch above).
        const brownBorder = !isSelectedPiece && !this.constructionActive;
        leather.strokeColor = new this.scope.Color(
          isSelectedPiece
            ? this.leatherSelectionStroke()
            : brownBorder
              ? LEATHER_BORDER()
              : SHAPE_STROKE(),
        );
        leather.strokeScaling = false;
        // Construction-editing context: unfilled outline so the scaffold
        // and the parts read together, with a heavier solid stroke so the
        // outline stays legible without a fill behind it (#649 follow-up).
        leather.strokeWidth = isSelectedPiece
          ? 1.5
          : this.constructionActive
            ? 1.2
            : LEATHER_BORDER_WIDTH;
        leather.fillColor = this.constructionActive ? null : SHAPE_FILL();
        leather.data = { kind: "piece", pieceId: piece.id, role: "shape" };
        group.addChild(leather);
      }
    }

    const visibleDecorations = piece.decorations.filter((d) => d.visible);
    if (visibleDecorations.length > 0) {
      const decGroup = new this.scope.Group();
      decGroup.data = { kind: "decorations", pieceId: piece.id };
      // Clip decorations to the piece's actual leather (outline minus holes
      // and cutouts) so stamps don't bleed past edges or over voids.
      const clip = this.buildLeatherClip(piece);
      if (clip) {
        clip.clipMask = true;
        decGroup.addChild(clip);
      }
      for (const decoration of visibleDecorations) {
        const item = this.renderDecoration(piece, decoration);
        if (item) decGroup.addChild(item);
      }
      group.addChild(decGroup);
    }

    // Stitching holes render on top of the leather and decorations as marks
    // (not cutouts). Wrap them in a clipped container so any portion that
    // would fall outside the leather (or over a hole / cutout) is hidden —
    // a slot near the edge can extend conceptually past the boundary, but
    // only the portion that lands on actual leather should be visible.
    const visibleStitches = pieceStitchLines(piece).filter((l) => isVisible(l));
    if (visibleStitches.length > 0) {
      const container = new this.scope.Group();
      container.data = { kind: "stitching-clip", pieceId: piece.id };
      const clip = this.buildLeatherClip(piece);
      if (clip) {
        clip.clipMask = true;
        container.addChild(clip);
      }
      for (const line of visibleStitches) {
        // Highlight every stitch in the multi-select set (#199 path B). The set
        // can span pieces, so it's matched by id alone; the legacy single-lead
        // check stays as the fallback when no set is present.
        const isSelectedStitch =
          sel.stitchIds && sel.stitchIds.length > 0
            ? sel.stitchIds.includes(line.id)
            : sel.pieceId === piece.id && sel.stitchId === line.id;
        const stitchGroup = this.buildStitchLineGroup(
          piece,
          line,
          isSelectedStitch,
        );
        if (stitchGroup) container.addChild(stitchGroup);
      }
      group.addChild(container);
      // Point markers + letters go on TOP of the clipped container, so they
      // overlay the part instead of being punched into it (#693).
      for (const line of visibleStitches) {
        const overlay = this.buildStitchMarkOverlay(piece, line);
        if (overlay) group.addChild(overlay);
      }
    }

    // Hardware sits on top of everything else — caps physically protrude
    // above the leather, decorations, and stitching. Each entry is bound
    // to a hole on the same piece (the Hardware tool guarantees this);
    // we silently skip orphaned entries (hole was deleted but the
    // hardware wasn't yet cleaned up).
    const visibleHardware = pieceHardware(piece).filter((h) => isVisible(h));
    if (visibleHardware.length > 0) {
      const hwGroup = new this.scope.Group();
      hwGroup.data = { kind: "hardware-group", pieceId: piece.id };
      for (const hw of visibleHardware) {
        const hole = piece.holes.find((h) => h.id === hw.holeId);
        if (!hole) continue;
        const item = this.buildHardwareItem(
          hw,
          hole.position.x,
          hole.position.y,
        );
        if (item) hwGroup.addChild(item);
      }
      group.addChild(hwGroup);
    }

    // Cut-layout transform (#652): in Spread view a linked part is drawn at its
    // `layout` offset (translation only for now). The whole group moves, so
    // shape + holes + cutouts + stitch + decorations stay together, and
    // Paper's hit-test respects the transform for free. Assembled view ignores
    // it. Unlinked parts have no `layout`, so this is a no-op for them.
    if (this.partLayoutView === "spread") {
      const layout = piece.constructionOutline?.layout;
      if (layout && (layout.dx !== 0 || layout.dy !== 0)) {
        group.translate(new this.scope.Point(layout.dx, layout.dy));
      }
    }

    return group;
  }

  /** True when handle dots will be rendered on this line — same gate
   *  used by the handle render loop. Derived (offset) lines never get
   *  handles. */

  private buildHardwareItem(
    hw: Hardware,
    cx: number,
    cy: number,
  ): paper.Item | null {
    let path: paper.CompoundPath;
    try {
      path = new this.scope.CompoundPath({
        pathData: hardwarePathData(hw.type, hw.sizeMm, hw.side ?? "female"),
      });
    } catch {
      return null;
    }
    const fill = HARDWARE_COLORS[hw.color];
    path.fillColor = new this.scope.Color(fill);
    path.strokeColor = new this.scope.Color(shadeHex(fill, -0.35));
    path.strokeWidth = 0.5;
    path.strokeScaling = false;
    path.position = new this.scope.Point(cx, cy);
    path.data = { kind: "hardware", pieceId: hw.holeId, hardwareId: hw.id };
    return path;
  }

  /** Build a path representing the actual leather of a piece: outer outline
   *  with holes and cutouts subtracted. Used as a clip mask for decorations.
   *  All temporary paths are constructed with `insert: false` so they don't
   *  leak into the project tree; the returned region is detached and ready to
   *  be added to a parent group. Delegates to the shared `buildLeatherRegion`
   *  (#910) so this clip and the stitch-hole retention filter use one identical
   *  region definition — output is byte-identical to the inlined version. */
  private buildLeatherClip(piece: Piece): paper.PathItem | null {
    return buildLeatherRegion(piece, this.scope);
  }

  /** Build a Paper.js group representing one stitch line. The seam-path
   *  hairline is an editing aid — only drawn in structural-edit mode (i.e.,
   *  while the Stitch tool is active). The hole marks are always rendered
   *  as opaque black shapes so they stand out against the leather. The
   *  whole group carries `data.kind === "stitch"` so a single drag moves
   *  every visual together. */
  private buildStitchLineGroup(
    piece: Piece,
    line: StitchLine,
    selected: boolean,
  ): paper.Item | null {
    if (line.points.length < 2) return null;
    const wrap = new this.scope.Group();
    wrap.data = { kind: "stitch", pieceId: piece.id, stitchId: line.id };

    // Retain only the holes that actually land on this piece's leather (#910):
    // filter placements in ORIGINAL space FIRST (before any display translate),
    // then build the marks — so overflow holes past the piece edge are dropped
    // outright instead of merely clipped, and no dangling off-pitch connector is
    // built for a hole that no longer exists.
    const marks = marksFromPlacements(
      line,
      placedStitchHoles(line, piece, this.scope),
      this.jointMarksFor(line.id),
    );
    // Off-pitch holes (#687, matching on): the seam's selection line is drawn
    // as the WAVY hole-connector (straight on-pitch, wavy where the follower
    // can't sit on the matched pitch) instead of a straight line, so the wave
    // IS the line rather than an extra overlay.
    const offPitch =
      isFeatureEnabled("stitch-matching") && hasOffPitchHoles(marks);

    // The seam-path hairline is an editing aid drawn while the Stitch tool is
    // active and in the construction-editing context (#649). It's ALSO drawn
    // whenever the seam is selected, so a selected seam reads as a line — not
    // just its holes — even on otherwise-finished leather.
    if (selected || this.editMode === "stitch" || this.constructionActive) {
      // Built from `seamPathData` so a CURVED seam's guide is the curve (#982)
      // — segments-from-points would draw the chords between its anchors and
      // quietly disagree with where the holes actually land.
      const guide = new this.scope.Path({
        pathData: seamPathData(line),
      });
      // The seam-path hairline shows the FULL outline as context, so a closed
      // loop's guide closes back to the start even when only an arc of it is
      // worked — otherwise the un-drawn closing segment leaves a gap at the
      // seam's wrap point (unrelated to where the working area was trimmed).
      // The thicker overlay on top marks the actually-worked span (an arc for
      // an inset-windowed loop, #704), the same way an open seam's guide shows
      // its full length including the un-worked inset tails.
      if (isStitchLoop(line)) guide.closed = true;
      guide.strokeColor = new this.scope.Color(
        selected ? this.leatherSelectionStroke() : STITCH_GUIDE_STROKE(),
      );
      guide.strokeColor.alpha = selected ? 0.9 : 0.7;
      guide.strokeWidth = selected ? 1 : 0.5;
      guide.strokeScaling = false;
      guide.fillColor = null;
      wrap.addChild(guide);

      // On selection, overlay the WORKING-AREA span (where the holes actually
      // land) as a slightly thicker line so it's easy to pick out from the
      // inset tails that carry no holes — and when the follower is off-pitch,
      // that thicker line is the wavy hole-connector (#687).
      if (selected) {
        let work: paper.PathItem | null = null;
        if (offPitch) {
          const d = stitchConnectorPath(
            marks,
            stitchRendersClosed(line),
            line.matchSpacing,
          );
          work = d
            ? new this.scope.CompoundPath({ pathData: d, insert: false })
            : null;
          if (work) work.data = { kind: "off-pitch-wave" };
        } else {
          work = this.stitchWorkingSubPath(line);
          // Say WHERE the spacing is off and which way (#691): compressed gaps
          // wave, stretched ones go thin. Only when something actually deviates
          // — otherwise the overlay is drawn exactly as it always was.
          const split =
            work instanceof this.scope.Path
              ? this.splitWorkByPitch(work, marks, effectiveStitchSpacing(line))
              : null;
          if (split) {
            for (const part of split) {
              part.path.strokeColor = new this.scope.Color(
                this.leatherSelectionStroke(),
              );
              part.path.strokeColor.alpha = 0.95;
              part.path.strokeWidth = part.width;
              part.path.strokeScaling = false;
              part.path.fillColor = null;
              part.path.data = { kind: part.kind };
              wrap.addChild(part.path);
            }
            work = null;
          }
        }
        if (work) {
          work.strokeColor = new this.scope.Color(
            this.leatherSelectionStroke(),
          );
          work.strokeColor.alpha = 0.95;
          work.strokeWidth = 2.5;
          work.strokeScaling = false;
          work.fillColor = null;
          wrap.addChild(work);
        }
      }
    }

    for (const mark of marks) {
      let cp: paper.PathItem;
      try {
        cp = new this.scope.CompoundPath({ pathData: mark.d, insert: false });
      } catch {
        continue;
      }
      cp.strokeColor = selected
        ? new this.scope.Color(this.leatherSelectionStroke())
        : null;
      cp.strokeWidth = selected ? 1.2 : 0;
      cp.strokeScaling = false;
      // In the construction-editing context the holes read construction-style
      // in the line colour (yellow) alongside the seam guide (#649); otherwise
      // opaque black so the punched marks read solid on the finished leather.
      cp.fillColor = this.constructionActive
        ? new this.scope.Color(CONSTRUCTION_STROKE())
        : new this.scope.Color(0, 0, 0, 1);
      wrap.addChild(cp);

      // Main-side skip mark (#687, matching on): a slash through every Nth hole
      // — the one the follower drops — flagging it to skip when sewing.
      if (mark.skipD && isFeatureEnabled("stitch-matching")) {
        let slash: paper.PathItem;
        try {
          slash = new this.scope.CompoundPath({
            pathData: mark.skipD,
            insert: false,
          });
        } catch {
          continue;
        }
        slash.strokeColor = new this.scope.Color(
          this.constructionActive ? CONSTRUCTION_STROKE() : "#000000",
        );
        slash.strokeWidth = 0.6;
        slash.strokeScaling = false;
        slash.fillColor = null;
        slash.data = { kind: "skip-slash" };
        wrap.addChild(slash);
      }

      // The point markers + letters (#693) are NOT built here — they are an
      // overlay OVER the part, not a mark punched into it, so they live outside
      // the leather clip. See `buildStitchMarkOverlay`.
    }

    return wrap;
  }

  /**
   * The POINT MARKERS and LETTERS for one seam (#693), as an overlay.
   *
   * Deliberately not part of the stitch group: that lives inside the
   * leather-clipped container, which is right for holes and slashes — they are
   * punched INTO the part, so anything past its edge should be hidden. These
   * are annotations ABOUT the part, pointing at a hole from beside the row, so
   * clipping them chopped the chevron in half on a seam that runs near an edge
   * and swallowed the letter outright.
   *
   * So they render like a guide ray: over the piece, unclipped, in the
   * leather-context accent — the near-black that reads on the tan fill, since
   * the light canvas accent washes out over leather (#744).
   */
  private buildStitchMarkOverlay(
    piece: Piece,
    line: StitchLine,
  ): paper.Item | null {
    if (!isFeatureEnabled("stitch-matching")) return null;
    if (line.points.length < 2) return null;
    const marks = marksFromPlacements(
      line,
      placedStitchHoles(line, piece, this.scope),
      this.jointMarksFor(line.id),
    );
    const lettersOn = isFeatureEnabled("point-letters");
    const ink = new this.scope.Color(
      this.constructionActive
        ? CONSTRUCTION_STROKE()
        : this.leatherSelectionStroke(),
    );
    const group = new this.scope.Group();
    group.data = { kind: "stitch-marks", pieceId: piece.id, stitchId: line.id };
    const add = (d: string, kind: string, extra: Record<string, unknown>) => {
      let item: paper.PathItem;
      try {
        item = new this.scope.CompoundPath({ pathData: d, insert: false });
      } catch {
        return;
      }
      item.strokeColor = ink;
      item.strokeWidth = 1.1;
      item.strokeScaling = false;
      // Outline only — a filled glyph would read as geometry on the part.
      item.fillColor = null;
      item.data = { kind, pieceId: piece.id, stitchId: line.id, ...extra };
      group.addChild(item);
    };
    for (const mark of marks) {
      if (mark.startD) add(mark.startD, "stitch-start", {});
      if (mark.pairLabelD && lettersOn)
        add(mark.pairLabelD, "stitch-pair-label", {
          pairLabel: mark.pairLabel,
        });
    }
    return group.children.length > 0 ? group : (group.remove(), null);
  }

  /** The seam's WORKING-AREA span as its own (uninserted) Paper path — the
   *  portion `[lo, hi]` of the polyline that actually carries holes, used to
   *  draw it thicker on selection. Open seams are trimmed to `[lo, hi]`; a
   *  loop that stitches as a continuous ring returns the whole (closed)
   *  perimeter; an inset-windowed loop (#704) returns just its worked arc, with
   *  the ring's `startOffset` + wrap handled. Returns null when there's no
   *  positive-length working span. */
  /**
   * Split the selected seam's working overlay by how each gap compares with the
   * row's pitch (#691), so the drawing says WHERE the spacing is off and which
   * way instead of looking identical to an even row.
   *
   * Derived FROM `work` by sampling rather than rebuilt from hole positions:
   * the overlay has to keep the seam's real curvature, its working-window
   * extent (which runs past the first and last hole into the insets) and its
   * open-or-closed topology. An earlier version rebuilt it as straight chords
   * hole to hole and quietly lost all three.
   *
   * Returns null when nothing deviates, and the caller draws `work` untouched —
   * the overwhelmingly common case, and the one every existing overlay test
   * describes.
   */
  private splitWorkByPitch(
    work: paper.Path,
    marks: ReadonlyArray<{ position: { x: number; y: number } }>,
    pitch: number,
    tolerance = 0.01,
  ): Array<{ path: paper.Path; width: number; kind: string }> | null {
    if (marks.length < 2 || !(pitch > 0) || !(work.length > 0)) return null;
    const len = work.length;
    const offsetOf = (p: { x: number; y: number }) =>
      work.getNearestLocation(new this.scope.Point(p.x, p.y))?.offset ?? null;
    const offs: number[] = [];
    for (const m of marks) {
      const o = offsetOf(m.position);
      if (o == null) return null;
      offs.push(o);
    }
    // Walking order along the overlay, not hole order: a ring's row may start
    // anywhere on it.
    offs.sort((a, b) => a - b);
    const cuts: Array<{ from: number; to: number; kind: string }> = [];
    let deviates = false;
    // Lead-in / lead-out: the window runs past the outer holes into the insets,
    // and that stretch carries no gap to judge — it is drawn as ordinary line.
    if (offs[0]! > 1e-6) cuts.push({ from: 0, to: offs[0]!, kind: "even" });
    for (let i = 0; i + 1 < offs.length; i++) {
      const a = offs[i]!;
      const b = offs[i + 1]!;
      const gap = b - a;
      const kind =
        Math.abs(gap - pitch) <= tolerance
          ? "even"
          : gap < pitch
            ? "compressed"
            : "stretched";
      if (kind !== "even") deviates = true;
      cuts.push({ from: a, to: b, kind });
    }
    if (len - offs[offs.length - 1]! > 1e-6)
      cuts.push({ from: offs[offs.length - 1]!, to: len, kind: "even" });
    if (!deviates) return null;

    const out: Array<{ path: paper.Path; width: number; kind: string }> = [];
    for (const c of cuts) {
      const span = c.to - c.from;
      if (span <= 1e-6) continue;
      const steps = Math.max(2, Math.ceil(span / 0.4));
      const path = new this.scope.Path({ insert: false });
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const at = c.from + t * span;
        const pt = work.getPointAt(Math.min(len, at));
        if (!pt) continue;
        if (c.kind === "compressed") {
          // A wave whose ARC LENGTH is the pitch: the line carries exactly the
          // thread the squeezed gap is missing. Same idiom as a matched
          // follower's over-tight stitches (#687).
          const n = work.getNormalAt(Math.min(len, at));
          const cycles = 1;
          const amp = Math.min(0.9, (pitch - span) * 0.9);
          const off = amp * Math.sin(t * cycles * 2 * Math.PI);
          path.add(pt.add(n ? n.multiply(off) : new this.scope.Point(0, 0)));
        } else {
          path.add(pt);
        }
      }
      if (path.segments.length < 2) continue;
      // Stretched reads THIN — a line pulled longer is a line pulled thinner.
      out.push({
        path,
        width: c.kind === "stretched" ? 1 : 2.5,
        kind: `pitch-${c.kind}`,
      });
    }
    return out.length ? out : null;
  }

  private stitchWorkingSubPath(line: StitchLine): paper.Path | null {
    if (line.points.length < 2) return null;
    // Walk the seam in its stored direction (#796) so the working-area overlay
    // lands on the SAME span as the holes — which also route through
    // `directedStitchLine`. Without this a "ccw" seam draws its worked span at
    // the opposite end from where the holes actually sit.
    line = directedStitchLine(line);
    const total = stitchLineLength(line);
    if (!(total > 0)) return null;
    if (stitchRendersClosed(line)) {
      const full = new this.scope.Path({
        pathData: seamPathData({ ...line, closed: true }),
        insert: false,
      });
      full.closed = true;
      return full;
    }
    if (isStitchLoop(line)) {
      // Windowed loop: extract the worked arc from the ring. Walk the vertices
      // TWICE (an open path spanning 2× the perimeter) so a window that wraps
      // past the start-of-path origin is a single continuous slice, then trim
      // to [winStart, winStart + winLen] with the same split as an open seam.
      const { lo, hi } = stitchWorkingWindow(line, total);
      const winLen = hi - lo;
      if (!(winLen > 1e-6)) return null;
      const off = line.startOffset ?? 0;
      const winStart = (((off + lo) % total) + total) % total;
      // Two laps as one open path, curves included (#982), so a window that
      // wraps past the start-of-path origin is a single continuous slice.
      const ring2 = new this.scope.Path({
        pathData: seamPathDataLaps(line, 2),
        insert: false,
      });
      const tail = ring2.splitAt(ring2.getLocationAt(winStart + winLen));
      tail?.remove();
      const work = ring2.splitAt(ring2.getLocationAt(winStart));
      ring2.remove();
      return work ?? null;
    }
    const full = new this.scope.Path({
      pathData: seamPathData(line),
      insert: false,
    });
    const { lo, hi } = stitchWorkingWindow(line, total);
    if (!(hi > lo + 1e-6)) {
      full.remove();
      return null;
    }
    // Trim to [lo, hi]: drop the tail after `hi`, then split off the head
    // before `lo`, keeping the middle.
    const afterHi = full.splitAt(full.getLocationAt(hi));
    afterHi?.remove();
    const work = full.splitAt(full.getLocationAt(lo));
    full.remove();
    return work ?? null;
  }

  private renderDecoration(piece: Piece, dec: Decoration): paper.Item | null {
    const isSelected =
      this.selection.pieceId === piece.id &&
      this.selection.decorationId === dec.id;
    let item: paper.Item | null = null;
    switch (dec.kind) {
      case "stamp":
        item = this.renderStamp(
          dec.stampId,
          dec.position,
          dec.rotation,
          dec.scale,
        );
        break;
      case "lineStamp":
        item = this.renderLineStamp(dec);
        break;
      case "fill":
        item = this.renderFill(dec);
        break;
      case "offset":
        item = this.renderOffset(dec);
        break;
      case "freePath": {
        const p = this.parsePath(dec.pathData);
        if (p) {
          p.strokeColor = new this.scope.Color(DECOR_STROKE());
          p.strokeWidth = dec.strokeWidth;
          p.closed = dec.closed;
          item = p;
        }
        break;
      }
    }
    if (!item) return null;
    item.data = {
      kind: "decoration",
      pieceId: piece.id,
      decorationId: dec.id,
    };
    if (isSelected) {
      const bounds = item.bounds;
      const sel = new this.scope.Path.Rectangle({
        rectangle: bounds.expand(4),
        strokeColor: new this.scope.Color(this.leatherSelectionStroke()),
        strokeWidth: 1,
        dashArray: [4, 2],
      });
      sel.data = { kind: "selection-box" };
      const wrapper = new this.scope.Group([item, sel]);
      wrapper.data = item.data;
      return wrapper;
    }
    return item;
  }

  private renderStamp(
    stampId: string,
    position: { x: number; y: number },
    rotation: number,
    scale: number,
  ): paper.Item | null {
    const proto = this.symbols.get(stampId);
    const meta = this.library.get(stampId);
    if (!proto || !meta) return null;
    const clone = proto.clone({ insert: false });
    this.normalizeStrokes(clone);
    const cx = meta.bbox.x + meta.bbox.w / 2;
    const cy = meta.bbox.y + meta.bbox.h / 2;
    clone.translate(new this.scope.Point(-cx, -cy));
    clone.scale(scale, scale, new this.scope.Point(0, 0));
    if (rotation) {
      clone.rotate(rotation, new this.scope.Point(0, 0));
    }
    clone.translate(new this.scope.Point(position.x, position.y));
    return clone;
  }

  private renderLineStamp(
    dec: Extract<Decoration, { kind: "lineStamp" }>,
  ): paper.Item | null {
    const meta = this.library.get(dec.stampId);
    if (!meta) return null;
    const path = this.parsePath(dec.pathData);
    if (!path) return null;
    path.closed = dec.closed;
    const placements = placeStampsAlongPath(path, meta, {
      spacing: dec.spacing,
      scale: dec.scale,
      rotateAlong: dec.rotateAlong,
      closed: dec.closed,
    });
    const group = new this.scope.Group();
    // Carry the path as a guide (visible only when selected), keep it for hit-testing.
    path.strokeColor = new this.scope.Color(0.85, 0.6, 0.3, 0.2);
    path.strokeWidth = 0.6;
    path.fillColor = null;
    path.dashArray = [2, 2];
    group.addChild(path);
    for (const place of placements) {
      const stamp = this.renderStamp(
        dec.stampId,
        { x: place.x, y: place.y },
        place.rotation,
        place.scale,
      );
      if (stamp) group.addChild(stamp);
    }
    return group;
  }

  private renderFill(
    dec: Extract<Decoration, { kind: "fill" }>,
  ): paper.Item | null {
    const meta = this.library.get(dec.stampId);
    if (!meta || !meta.tile) return null;
    const region = this.parsePath(dec.regionPathData);
    if (!region) return null;
    region.closed = true;
    const placements = tileRegion(region, meta, {
      rotation: dec.rotation,
      scale: dec.scale,
    });
    const group = new this.scope.Group();
    region.strokeColor = new this.scope.Color(0.85, 0.6, 0.3, 0.25);
    region.strokeWidth = 0.6;
    region.fillColor = null;
    region.dashArray = [3, 3];
    group.addChild(region);

    const clipMask = region.clone({ insert: false });
    clipMask.clipMask = true;
    const clipGroup = new this.scope.Group([clipMask]);
    for (const place of placements) {
      const stamp = this.renderStamp(
        dec.stampId,
        { x: place.x, y: place.y },
        place.rotation,
        place.scale,
      );
      if (stamp) clipGroup.addChild(stamp);
    }
    group.addChild(clipGroup);
    return group;
  }

  private renderOffset(
    dec: Extract<Decoration, { kind: "offset" }>,
  ): paper.Item | null {
    const source = this.parsePath(dec.sourcePathData);
    if (!source) return null;
    try {
      const offset = PaperOffset.offset(source, dec.distance, {
        join: dec.joinType,
        cap: "butt",
      });
      offset.strokeColor = new this.scope.Color(DECOR_STROKE());
      offset.strokeWidth = 0.8;
      offset.fillColor = null;
      source.remove();
      return offset;
    } catch (e) {
      console.warn("offset failed", e);
      source.strokeColor = new this.scope.Color(DECOR_STROKE());
      return source;
    }
  }

  private parsePath(d: string): paper.Path | paper.CompoundPath | null {
    if (!d) return null;
    try {
      const path = new this.scope.CompoundPath(d);
      // Reduce to single Path when possible for nicer math.
      if (path.children?.length === 1) {
        const single = path.children[0] as paper.Path;
        single.remove();
        path.remove();
        return single;
      }
      return path;
    } catch (e) {
      console.warn("parsePath failed", d, e);
      return null;
    }
  }

  private normalizeStrokes(item: paper.Item) {
    item.strokeScaling = false;
    if ("strokeWidth" in item && (item as paper.Path).strokeWidth) {
      (item as paper.Path).strokeWidth = Math.min(
        (item as paper.Path).strokeWidth || 0,
        0.6,
      );
    }
    if (item.children) {
      for (const child of item.children) this.normalizeStrokes(child);
    }
  }

  /** Accent for selection highlights on leather-bound objects (part outline,
   *  holes, cutouts, seams, decorations). Over filled leather the pale fill
   *  washes out the light accent, so use the darker leather accent — but in the
   *  construction-editing context parts render as an unfilled scaffold over the
   *  mat, where the light accent still reads best, so keep it there. */
  private leatherSelectionStroke(): string {
    return this.constructionActive
      ? SELECTION_STROKE()
      : LEATHER_SELECTION_STROKE();
  }
}
